import type {
  RaAdditionalControl,
  RaExistingControl,
  RaLibraryCheck,
  RaLibraryContent,
  RaLibraryRow,
  RaLibraryStatus,
  RaPublishIssue,
} from '@felix/domain';
import { localId } from '../view.js';
import type { RaAssignment, RaLibraryItem, RaVesselVersion } from './types.js';

/**
 * Risk ▸ Library ekranının saf yardımcıları (K3; ADR 0087): süzgeç, özet, yeni satır ve önlem,
 * sorun metninin satır numarası. Kural kararları domain'dedir (`raLibraryIssues`); burada yalnız
 * görünüm.
 */

export interface LibraryFilter {
  query: string;
  status: RaLibraryStatus | 'all';
}
export const EMPTY_LIBRARY_FILTER: LibraryFilter = { query: '', status: 'all' };

const norm = (value: string | null | undefined) => (value ?? '').toLocaleLowerCase();

export function filterLibrary(
  items: readonly RaLibraryItem[],
  filter: LibraryFilter,
): RaLibraryItem[] {
  const q = norm(filter.query.trim());
  return items.filter(
    (i) =>
      (filter.status === 'all' || i.status === filter.status) &&
      (q === '' ||
        norm(i.code).includes(q) ||
        norm(i.title).includes(q) ||
        norm(i.activityCode).includes(q)),
  );
}

export function filterVesselLibrary(
  items: readonly RaVesselVersion[],
  query: string,
): RaVesselVersion[] {
  const q = norm(query.trim());
  return q === ''
    ? [...items]
    : items.filter((i) => norm(i.code).includes(q) || norm(i.title).includes(q));
}

/** Özet kartlarının sayıları (ofis). */
export function libraryStats(items: readonly RaLibraryItem[]) {
  const published = items.filter((i) => i.publishedRevision !== null);
  return {
    published: published.length,
    migrated: items.filter((i) => i.migrated).length,
    workflow: items.filter((i) => ['submitted', 'under_review', 'approved'].includes(i.status))
      .length,
    review: items.filter((i) => i.status === 'submitted' || i.status === 'under_review').length,
    drafts: items.filter((i) => i.status === 'draft' || i.status === 'returned').length,
    missing: items
      .filter((i) => i.status === 'draft' || i.status === 'returned')
      .reduce((n, i) => n + i.missingResidual, 0),
    assigned: published.filter((i) => i.assignments.length > 0).length,
  };
}

/** Sorunun satır numarası (1'den); genel alan sorununda `null`. */
export function issueRow(content: RaLibraryContent, issue: RaPublishIssue): number | null {
  if (!issue.rowId) return null;
  const index = content.rows.findIndex((r) => r.id === issue.rowId);
  return index === -1 ? null : index + 1;
}

/** Sorunun gösterileceği sekme. */
export function issueTab(code: RaPublishIssue['code']): string {
  if (code.startsWith('REVIEW') || code.startsWith('PREPARED')) return 'general';
  if (code === 'ROWS_REQUIRED' || code === 'HAZARD_REQUESTED') return 'rows';
  if (code.startsWith('CURRENT')) return 'existing';
  if (code.startsWith('ADDITIONAL')) return 'additional';
  return 'residual';
}

export function blankRow(step = ''): RaLibraryRow {
  return {
    id: localId(),
    linkId: null,
    stepId: null,
    step,
    hazardId: null,
    hazard: '',
    hazardRequested: false,
    consequenceIds: [],
    consequence: null,
    exposed: null,
    initial: { likelihood: 3, severity: 3, rationale: null, assumptions: null },
    current: null,
    existingControls: [],
    additionalControls: [],
    residual: null,
  };
}

/** Satırın kopyası: yeni kimlikler; kütüphane bağı ve kalan risk kopyalanmaz. */
export function duplicateRow(row: RaLibraryRow): RaLibraryRow {
  return {
    ...row,
    id: localId(),
    linkId: null,
    existingControls: row.existingControls.map((c) => ({ ...c, id: localId() })),
    additionalControls: row.additionalControls.map((c) => ({ ...c, id: localId() })),
    residual: null,
  };
}

export function blankExisting(): RaExistingControl {
  return {
    id: localId(),
    controlId: null,
    measure: '',
    hierarchy: [],
    requirement: null,
    procedure: null,
    owner: null,
    evidence: null,
    inPlace: 'unknown',
    effectiveness: null,
  };
}

/** Yeni faaliyete özel operasyon maddesi (K5; ADR 0089 §2). */
export function blankCheck(): RaLibraryCheck {
  return { id: localId(), text: '', naAllowed: false };
}

export function blankAdditional(): RaAdditionalControl {
  return {
    id: localId(),
    controlId: null,
    measure: '',
    hierarchy: [],
    expectedEffect: null,
    effectRationale: null,
    responsibleRole: null,
    dueDay: null,
    evidence: null,
    verifier: null,
    completionCriteria: null,
    status: 'draft',
  };
}

/** Boş metni `null`'a çevirir (şema boşu `null` yazar; kirli denetimi aynı kalsın). */
export function textOrNull(value: string): string | null {
  return value.trim() === '' ? null : value;
}

/** Atamanın kısa adı: "Bütün CARGO" ya da geminin adı. */
export function assignmentLabel(
  a: Pick<RaAssignment, 'vesselGroup' | 'vesselName' | 'vesselId'>,
  groupName: (group: string) => string,
): string {
  if (a.vesselGroup !== null) return groupName(a.vesselGroup);
  return a.vesselName ?? a.vesselId ?? '';
}
